import React from 'react';
import { Navigate, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAdminStore } from '../../store/adminStore';
import { adminApi } from '../../lib/api';
import { LayoutDashboard, Car, Route, DollarSign, Plus, List, LogOut, Menu, X, KeyRound } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';

export const AdminLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isAuthenticated = useAdminStore(state => state.isAuthenticated);
  const email = useAdminStore(state => state.email);
  const logout = useAdminStore(state => state.logout);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [pwDialogOpen, setPwDialogOpen] = useState(false);
  const [pwForm, setPwForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [pwLoading, setPwLoading] = useState(false);
  
  if (!isAuthenticated) {
    return <Navigate to="/admin/login" replace />;
  }
  
  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };
  
  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (pwForm.new_password.length < 8) {
      toast.error('La nueva contraseña debe tener al menos 8 caracteres');
      return;
    }
    if (pwForm.new_password !== pwForm.confirm_password) {
      toast.error('Las contraseñas no coinciden');
      return;
    }
    
    setPwLoading(true);
    try {
      await adminApi.changePassword({
        current_password: pwForm.current_password,
        new_password: pwForm.new_password
      });
      toast.success('Contraseña actualizada exitosamente');
      setPwDialogOpen(false);
      setPwForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      const detail = error.response?.data?.detail || 'Error al cambiar contraseña';
      toast.error(detail);
    } finally {
      setPwLoading(false);
    }
  };
  
  const menuItems = [
    { path: '/admin/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/admin/vehicles', icon: Car, label: 'Vehículos' },
    { path: '/admin/routes', icon: Route, label: 'Rutas' },
    { path: '/admin/rates', icon: DollarSign, label: 'Tarifas' },
    { path: '/admin/addons', icon: Plus, label: 'Extras' },
    { path: '/admin/reservations', icon: List, label: 'Reservas' },
  ];
  
  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Sidebar */}
      <aside className={`
        w-64 bg-navy-950 text-indigo-50 fixed h-full z-30 transition-transform duration-300
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        md:translate-x-0
      `}>
        <div className="p-6">
          <h2 className="font-manrope font-bold text-xl mb-1">Sam's Transfer</h2>
          <p className="text-sm text-indigo-300">Panel Admin</p>
        </div>
        
        <nav className="px-3">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            
            return (
              <button
                key={item.path}
                onClick={() => {
                  navigate(item.path);
                  setSidebarOpen(false);
                }}
                className={`
                  w-full flex items-center gap-3 px-4 py-3 rounded-lg mb-1
                  transition-colors
                  ${isActive 
                    ? 'bg-navy-800 text-white' 
                    : 'text-indigo-100 hover:bg-navy-900'}
                `}
              >
                <Icon className="w-5 h-5" />
                <span className="font-medium">{item.label}</span>
              </button>
            );
          })}
        </nav>
        
        <div className="absolute bottom-0 w-full p-4 border-t border-navy-800">
          <div className="text-sm text-indigo-300 mb-2">{email}</div>
          <button
            onClick={() => setPwDialogOpen(true)}
            className="w-full flex items-center gap-2 px-4 py-2 mb-2 bg-navy-900 hover:bg-navy-800 
              rounded-lg transition-colors text-indigo-50"
          >
            <KeyRound className="w-4 h-4" />
            <span>Cambiar Contraseña</span>
          </button>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-2 px-4 py-2 bg-navy-900 hover:bg-navy-800 
              rounded-lg transition-colors text-indigo-50"
          >
            <LogOut className="w-4 h-4" />
            <span>Cerrar Sesión</span>
          </button>
        </div>
      </aside>
      
      {/* Mobile menu button */}
      <button
        onClick={() => setSidebarOpen(!sidebarOpen)}
        className="md:hidden fixed top-4 left-4 z-40 w-10 h-10 bg-navy-800 text-white 
          rounded-lg flex items-center justify-center"
      >
        {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
      </button>
      
      {/* Overlay */}
      {sidebarOpen && (
        <div 
          className="md:hidden fixed inset-0 bg-black/50 z-20"
          onClick={() => setSidebarOpen(false)}
        />
      )}
      
      {/* Main content */}
      <main className="flex-1 md:ml-64 p-4 md:p-8">
        <Outlet />
      </main>
      
      {/* Change Password Dialog */}
      <Dialog open={pwDialogOpen} onOpenChange={setPwDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-navy-700" />
              Cambiar Contraseña
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleChangePassword} className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-1.5 block">Contraseña Actual</label>
              <Input 
                type="password"
                value={pwForm.current_password}
                onChange={(e) => setPwForm({...pwForm, current_password: e.target.value})}
                placeholder="••••••••"
                required
                autoComplete="current-password"
              />
            </div>
            
            <div>
              <label className="text-sm font-medium mb-1.5 block">Nueva Contraseña</label>
              <Input 
                type="password"
                value={pwForm.new_password}
                onChange={(e) => setPwForm({...pwForm, new_password: e.target.value})}
                placeholder="Mínimo 8 caracteres"
                required
                minLength={8}
                autoComplete="new-password"
              />
            </div>
            
            <div>
              <label className="text-sm font-medium mb-1.5 block">Confirmar Nueva Contraseña</label>
              <Input 
                type="password"
                value={pwForm.confirm_password}
                onChange={(e) => setPwForm({...pwForm, confirm_password: e.target.value})}
                placeholder="Repetir nueva contraseña"
                required
                minLength={8}
                autoComplete="new-password"
              />
            </div>
            
            <div className="flex gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setPwDialogOpen(false)} className="flex-1">
                Cancelar
              </Button>
              <Button type="submit" disabled={pwLoading} className="flex-1 bg-navy-800 hover:bg-navy-900">
                {pwLoading ? 'Guardando...' : 'Actualizar'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
